import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';

import Button from 'components/helpers/Button/Button';
import Link from 'components/helpers/Link/Link';
import BackButton from 'components/Navigation/Header/BackButton';
import {
  type contextType,
  HeaderContext,
  type SubNavLinks,
  type SubNavSection,
} from 'components/Navigation/Header/Header';
import Key from 'lib/key';

import styles from './SubnavigationItem.module.scss';

const cx = classNames.bind(styles);

type SubnavigationItemProps = {
  index: number;
  isOpen: boolean;
  item: SubNavLinks | SubNavSection;
  parentId: string;
};

const SubnavigationItem = (props: SubnavigationItemProps): JSX.Element | null => {
  const { index, isOpen, item, parentId } = props;
  const { currentLang, handleBlur, isMobile, openSubNav, resetMobileMenu, setOpenSubNav } = React.useContext(
    HeaderContext
  ) as contextType;
  const { sitecoreContext: { pageEditing } = {} } = useSitecoreContext();

  const subNavItemId = `${parentId}-${index}`;
  const expanded = openSubNav?.startsWith(subNavItemId) || false;
  const parentExpanded = openSubNav === parentId;

  const handleBackButtonClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setOpenSubNav(parentId);
  };

  const handleButtonKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    const { key } = e;

    if (key === Key.Enter || key === Key.Space) {
      setOpenSubNav(subNavItemId);
    }
  };

  const handleButtonMouseDown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setOpenSubNav(subNavItemId);
  };

  const handleClick: React.MouseEventHandler<HTMLElement> = () => {
    resetMobileMenu();
    setOpenSubNav(null);
  };

  const renderSubNavButton = (): JSX.Element => {
    const { link, subNavSectionsAndLinks, title } = item as SubNavSection;

    const sectionId = `sub-nav-item-${index}`;
    const titleElement = <Text encode={pageEditing} field={title} tag="h6" />;

    const hasLinkData = link !== null && link?.value?.href !== '' && title?.value !== '';
    const linkElement = hasLinkData ? (
      <Link
        data-id="sublink"
        field={link}
        lang={currentLang}
        onBlur={handleBlur}
        showLinkTextWithChildrenPresent={false}
        tabIndex={isOpen || (isMobile && parentExpanded) ? 0 : -1}
        onClick={handleClick}
      >
        {titleElement}
      </Link>
    ) : (
      titleElement
    );

    const listElements = subNavSectionsAndLinks.map((listItem, listItemIndex) => (
      <SubnavigationItem
        key={`subnav-item-${index}-${listItemIndex}`}
        index={listItemIndex}
        isOpen={isOpen}
        item={listItem}
        parentId={subNavItemId}
      />
    ));

    return (
      <>
        <Button
          aria-controls={sectionId}
          aria-expanded={expanded}
          data-id="sublink"
          icon="carat"
          lang={currentLang}
          onBlur={handleBlur}
          onKeyDown={handleButtonKeyDown}
          onMouseDown={handleButtonMouseDown}
          tabIndex={isOpen || (isMobile && parentExpanded) ? 0 : -1}
        >
          {titleElement}
        </Button>
        <section className={cx({ 'subnavigation-section': true, expanded })} id={sectionId}>
          <BackButton controls={sectionId} onClick={handleBackButtonClick} visibile={expanded} />
          {linkElement}
          <ul>{listElements}</ul>
        </section>
      </>
    );
  };

  const renderSubNavLink = (): JSX.Element | null => {
    const { link, linkText } = item as SubNavLinks;

    const hasLinkData = link !== null && link?.value?.href !== '' && linkText?.value !== '';
    return hasLinkData ? (
      <Link
        data-id="sublink"
        field={link}
        icon={isMobile ? 'none' : 'carat'}
        lang={currentLang}
        onBlur={handleBlur}
        onClick={handleClick}
        showLinkTextWithChildrenPresent={false}
        tabIndex={isOpen || (isMobile && parentExpanded) ? 0 : -1}
      >
        <Text encode={pageEditing} field={linkText} />
      </Link>
    ) : null;
  };

  let renderedElement: JSX.Element | null = null;

  if ((item as SubNavSection)?.subNavSectionsAndLinks?.length) {
    renderedElement = renderSubNavButton();
  } else {
    renderedElement = renderSubNavLink();
  }

  return renderedElement ? (
    <li className={cx({ 'subnavigation-item': true })} key={`subnavigation-item-${index}`}>
      {renderedElement}
    </li>
  ) : null;
};

export default SubnavigationItem;
